import { useReducer, useEffect, useCallback, useContext } from 'react'
import { useRouter } from 'next/router'
import {
  initialState,
  reducer,
  fetchWalletData,
  getWalletDetails,
  storeVoucherCode,
  fetchWalletDataPDP,
  EGIFT_VOUCHER_GIFT_CARD,
} from './handlers'
import { EventTrackingContext } from '../../EventTrackingProvider/EventTrackingProvider'
import {
  eventsDataset,
  TRACKER_CONSTANTS,
  GTM_ENV_VAR as GEV,
} from '../utils/vouchersTracker'
import {
  AMP_EVENT_NAME as AEN,
  CTA_LOCATION_NAME,
} from '../../../lib/amplitude'
import { getConfigFor } from '../../../utils/configService'
import { SPLIT_FEATURES } from '../../../utils/constants'
import { Context } from '../../../components/CheckoutAddressProvider'
import { trackVoucherClickedEvent } from '../../../lib/analytics/events/voucherEvents/VoucherClickedEvent'
import routeController from '../../../routes'
import { EventScreenSection } from '../../../lib/analytics/core/EventScreenManager.ts'
import { BUSINESS_VERTICAL } from '../../../lib/analytics/constants/EventHelper'

const useVoucherTabHook = props => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const router = useRouter()
  const code =
    router.query?.code || router.query?.savePromoCode || router.query?.promoCode
  const filterFromUrl = router.query?.filter
  const vouchersActiveTab = router.query?.vouchersActiveTab
  const { track } = useContext(EventTrackingContext)
  const { checkoutAddress } = useContext(Context)

  const { configuration } = getConfigFor(SPLIT_FEATURES.VOUCHER_WALLET_SORTING)
  const { isEnabled: isVcControlEnabled } = getConfigFor(
    SPLIT_FEATURES.FE_EV_VOUCHER_CONTROL
  )
  const { isEnabled: isEGVChipFilterEnabled } = getConfigFor(
    SPLIT_FEATURES.FE_EGV_CHIP_FILTER
  )

  const { variantKey } = configuration

  useEffect(() => {
    track(eventsDataset[TRACKER_CONSTANTS.LANDED_VOUCHER_WALLET])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch({
      type: 'SET_ACTIVE_TAB',
      value: vouchersActiveTab === 'past' ? 1 : 0,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const storeVoucherCodeDetails = useCallback(
    async (isVoucher, isVcControlEnabledFlag) => {
      /* if there is no rewardType in deeplink, it is an E Gift Voucher */
      const isEvoucherDeepLink = !router.query?.deeplink?.includes('rewardType')

      storeVoucherCode(
        state.activeFilter,
        isVoucher,
        { code, dispatch },
        isEvoucherDeepLink,
        isVcControlEnabledFlag
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [code]
  )

  useEffect(() => {
    const isVoucher = router.query?.savePromoCode || router.query?.promoCode
    if (code && isVcControlEnabled) {
      const isMyinfoVerified =
        props.myinfoVerified === 'PARTIALLY_VERIFIED' ||
        props.myinfoVerified === 'FULLY_VERIFIED'
      if (isMyinfoVerified || props.phoneVerified) {
        if (!router.query?.deeplink?.includes('rewardType')) {
          getWalletDetails(code).then(res => {
            dispatch({
              type: 'GET_RECEIVER_VOUCHER_DETAILS',
              value: res,
            })
            dispatch({
              type: 'SHOW_ADD_EGIFT_VC_MODAL',
            })
            fetchWalletData('active', state.activeFilter, { dispatch }, '')
          })
        } else {
          storeVoucherCodeDetails(isVoucher, isVcControlEnabled)
        }
      } else {
        dispatch({
          type: 'SHOW_VERIFY_SIDE_SHEET',
        })
        fetchWalletData('active', state.activeFilter, { dispatch }, '')
      }
    } else if (code) {
      storeVoucherCodeDetails(isVoucher, isVcControlEnabled)
    }

    // !props?.ncVoucher - prevent API to call in this case as we are using the same hook for NCVOUCHER_SWIMLANE
    if (!code && !props?.productId && !props?.ncVoucher) {
      fetchWalletData(
        vouchersActiveTab || 'active',
        state.activeFilter,
        {
          dispatch,
        },
        variantKey
      )
    }

    if (!code && props?.productId && props?.storeId) {
      fetchWalletDataPDP(
        'active',
        props.storeId,
        {
          dispatch,
        },
        props.productId,
        variantKey,
        props?.categoryIds
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  useEffect(() => {
    const currentFilter = Object.values(state.activeFilter)[0]

    // Below if condition is triggered upon page load only if filter value is not All
    if (filterFromUrl !== currentFilter) {
      let filterIndex = -1
      if ('newFilters' in state.activeData) {
        filterIndex = state.activeData.newFilters.findIndex(
          x => x.name === filterFromUrl
        )
      }

      if (filterIndex > -1) {
        const mainFilterName = state.activeData.newFilters[filterIndex].name
        mapDispatch.handleFilter(
          filterIndex, // index 0, 1, 2
          mainFilterName, // All, Delivery, In-store, Kopitiam, Pay via app, pre-order, Scan & go
          vouchersActiveTab || 'active', // 0=use now tab or 1=past tab
          filterFromUrl
        )
      }
    }
  }, [filterFromUrl, state.activeData])

  useEffect(() => {
    const timer = setTimeout(
      () =>
        dispatch({
          type: 'HIDE_TOAST',
        }),
      4000
    )
    return () => clearTimeout(timer)
  }, [])

  const handleTrackVoucherClickedEvent = (item, index, swimlaneTitle) => {
    const match = routeController.match(window.location.href)
    let screenSection = null
    let screenName = null
    let businessVertical = null

    switch (true) {
      case match?.route?.name === 'account' && match?.query?.tab === 'vouchers':
        screenName = 'voucher | voucher wallet'
        screenSection = EventScreenSection.Chip(
          match?.query?.filter?.toLowerCase() || 'all'
        )
        break
      case match?.route?.name === 'product':
        screenSection = 'voucher-collection | Apply at checkout'
        businessVertical = BUSINESS_VERTICAL.GROCERY
        break
      case match?.parsedUrl?.pathname === '/':
        screenName = 'grocery-online | home'
        screenSection = `voucher-collection | ${swimlaneTitle}`
        businessVertical = BUSINESS_VERTICAL.GROCERY
        break
    }

    const params = {
      SCREEN_NAME: screenName,
      SCREEN_SECTION: screenSection,
      OFFER_ID: item?.id.toString() || '',
      OFFER_TYPE: item?.offerType || '',
      OFFER_SAP_REFERENCE: '', // not available
      VOUCHER_NAME: item?.detail?.title || '',
      VOUCHER_CODE: item?.code || '',
      VOUCHER_POSITION: index + 1,
      STORE_ID_SAP: checkoutAddress?.clientId || '',
      STORE_ID_DBP: checkoutAddress?.storeId?.toString() || '',
      STORE: checkoutAddress?.storeName || '',
      BUSINESS_VERTICAL: businessVertical,
    }
    trackVoucherClickedEvent(params)
  }

  const mapDispatch = {
    parentDispatch: dispatch,
    handleChange: async eventIndex => {
      dispatch({ type: 'SET_ACTIVE_TAB', value: eventIndex })
      if (state.pastData.length === 0) {
        dispatch({ type: 'LOADING' })
      }
      if (eventIndex === 1) {
        fetchWalletData('past', state.activeFilter, { dispatch })
        const event = {
          [GEV.AMPLITUDE_EVENT_NAME]: AEN.HISTORY_VOUCHERS_CLICKED,
          [GEV.ASSET_TYPE]: 'REWARDS',
          [GEV.AMPLITUDE_CTA_LOCATION]: {
            pageName: CTA_LOCATION_NAME.VOUCHER_WALLET,
          },
          [GEV.AMPLITUDE_CTA_SUBLOCATION]: null,
        }
        track(event)
      } else {
        fetchWalletData('active', state.activeFilter, { dispatch }, variantKey)
      }
      const activeTab = eventIndex === 1 ? 'past' : 'active'
      router.query.vouchersActiveTab = activeTab
      router.push(router, undefined, { shallow: true })
    },
    handleViewClick: async (voucher, index, swimlaneTitle = '') => {
      const { code: voucherCode, type } = voucher
      try {
        const results = await getWalletDetails(voucherCode)
        dispatch({ type: 'SHOW_VOUCHER_DETAILS', value: results })
        if (type === 'voucher') {
          track(
            eventsDataset[TRACKER_CONSTANTS.ON_VOUCHER_WALLET_EVOUCHER_DETAIL]
          )
        } else {
          track(
            eventsDataset[TRACKER_CONSTANTS.ON_VOUCHER_WALLET_PROMOCODE_DETAIL]
          )
        }
        handleTrackVoucherClickedEvent(voucher, index, swimlaneTitle)
      } catch (e) {
        dispatch({ type: 'SET_ERROR', value: e })
      }
    },
    handleFilter: (id, tag, tab, subFilter = '') => {
      const all = 'All (including Cashier/Self-checkout)'
      const tagState = subFilter === all ? 'In-store' : subFilter || tag

      // filter param should be either All or Delivery or In-store (no subFilters)
      router.query.filter = tag
      router.push(router, undefined, { shallow: true })
      const filteredTag = Object.fromEntries([[id, tagState]])
      let filteredData = []
      if (tagState !== 'All') {
        let walletData = state.activeData
        if (tab === 'past') {
          walletData = state.pastData
        }
        filteredData = walletData.list?.filter(voucher =>
          voucher.tags.find(value => value === tagState)
        )
        if (tagState === 'Delivery') {
          filteredData = walletData.list?.filter(voucher =>
            voucher.tags.find(value => value === 'Online')
          )
        }

        if (tagState === EGIFT_VOUCHER_GIFT_CARD && isEGVChipFilterEnabled) {
          filteredData = walletData.list?.filter(
            voucher => voucher.type === 'voucher'
          )
        }

        if (subFilter === all || tagState === 'In-store') {
          filteredData = walletData.list?.filter(voucher =>
            voucher.tags.find(value =>
              [tag, 'Pre-order', 'Pay via app'].includes(value)
            )
          )
        }
      }
      dispatch({
        type: 'SET_ACTIVE_FILTER_VALUE',
        filteredTag,
        filteredData,
      })
    },
    onHandleClose: () => dispatch({ type: 'HIDE_VOUCHER_DETAILS' }),
    onHandleEgiftModalClose: () =>
      dispatch({ type: 'CLOSE_ADD_EGIFT_VC_MODAL' }),
    handleCloseVerifySideSheet: () =>
      dispatch({ type: 'HIDE_VERIFY_SIDE_SHEET' }),
    onHandleAddEgiftVc: () => {
      storeVoucherCodeDetails(false, isVcControlEnabled)
      dispatch({ type: 'CLOSE_ADD_EGIFT_VC_MODAL' })
    },
    onHandleEgiftModalOpen: () => dispatch({ type: 'SHOW_ADD_EGIFT_VC_MODAL' }),
    onHandleReceiverVcDetails: res => {
      dispatch({
        type: 'GET_RECEIVER_VOUCHER_DETAILS',
        value: res,
      })
    },
    onHandleCloseCannotAddVcModal: () => {
      dispatch({
        type: 'HIDE_CANNOT_ADDVC_MODAL',
      })
    },
    track,
    eventsDataset,
    TRACKER_CONSTANTS,
    GEV,
  }

  return [state, mapDispatch]
}

export default useVoucherTabHook
